/**
 * Exports data as a CSV file download with UTF-8 BOM.
 */
export function exportCsv(
  headers: string[],
  rows: Array<Array<string | number | boolean | null | undefined>>,
  filename: string
): void {
  const headerRow = headers
    .map((h) => `"${String(h).replace(/"/g, '""')}"`)
    .join(",");
  const dataRows = rows.map((row) =>
    row
      .map((cell) => {
        if (cell === null || cell === undefined) return '""';
        return `"${String(cell).replace(/"/g, '""')}"`;
      })
      .join(",")
  );

  const csvContent = "\uFEFF" + [headerRow, ...dataRows].join("\n");
  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.setAttribute("href", url);
  link.setAttribute("download", filename);
  link.style.visibility = "hidden";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/**
 * Reads a File object as text with automatic UTF-8 / EUC-KR fallback encoding detection.
 */
export function readFileAsText(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const buffer = e.target?.result as ArrayBuffer;
      if (!buffer) {
        resolve("");
        return;
      }
      let text = new TextDecoder("utf-8", { fatal: false }).decode(buffer);
      if (text.includes("\uFFFD")) {
        try {
          const eucKrText = new TextDecoder("euc-kr").decode(buffer);
          if (!eucKrText.includes("\uFFFD")) {
            text = eucKrText;
          }
        } catch (err) {
          // keep utf-8 text fallback
        }
      }
      resolve(text);
    };
    reader.onerror = (err) => reject(err);
    reader.readAsArrayBuffer(file);
  });
}

/**
 * Parses a CSV string into a 2D matrix of strings, handling quotes, escapes, and line breaks.
 */
export function parseCsv(text: string): string[][] {
  let content = text || "";
  if (content.startsWith("\uFEFF")) {
    content = content.slice(1);
  }

  const rows: string[][] = [];
  let currentRow: string[] = [];
  let currentCell = "";
  let insideQuotes = false;

  for (let i = 0; i < content.length; i++) {
    const char = content[i];
    const nextChar = content[i + 1];

    if (char === '"') {
      if (insideQuotes && nextChar === '"') {
        currentCell += '"';
        i++;
      } else {
        insideQuotes = !insideQuotes;
      }
    } else if (char === "," && !insideQuotes) {
      currentRow.push(currentCell.trim());
      currentCell = "";
    } else if ((char === "\n" || char === "\r") && !insideQuotes) {
      if (char === "\r" && nextChar === "\n") {
        i++;
      }
      currentRow.push(currentCell.trim());
      if (currentRow.some((cell) => cell.length > 0)) {
        rows.push(currentRow);
      }
      currentRow = [];
      currentCell = "";
    } else {
      currentCell += char;
    }
  }

  if (currentCell.length > 0 || currentRow.length > 0) {
    currentRow.push(currentCell.trim());
    if (currentRow.some((cell) => cell.length > 0)) {
      rows.push(currentRow);
    }
  }

  return rows;
}
